import React from 'react';
import GoogleMap from 'google-map-react';
import config from 'config';

const MAP_ZOOM = 18;
const MIN_MAP_ZOOM = 10;
// The map pane is never wider than this in practice, so framing two points
// against it keeps both on screen whatever the window is doing.
const MAP_FIT_PIXELS = 300;
const EQUATOR_METERS_PER_PIXEL = 156543.03392;

// Number(null) is 0 and Number('') is 0, so the obvious Number.isFinite(Number(x))
// turns a missing coordinate into a real point off the coast of Africa. A
// rejection proposal has no new spot at all, and that is how it ended up drawing
// a marker for one.
const toNumber = value => {
  if (value === null || value === '' || typeof value === 'undefined') {
    return null;
  }

  const number = Number(value);

  return Number.isFinite(number) ? number : null;
};

const toPoint = source => {
  if (!source || typeof source !== 'object') {
    return null;
  }

  const lat = toNumber(source.lat);
  const lng = toNumber(source.lng);

  return lat === null || lng === null ? null : { lat, lng };
};

const getDistanceMeters = (left, right) => {
  const earthRadius = 6371000;
  const toRadians = degrees => (degrees * Math.PI) / 180;
  const latitudeDelta = toRadians(right.lat - left.lat);
  const longitudeDelta = toRadians(right.lng - left.lng);
  const haversine =
    Math.sin(latitudeDelta / 2) ** 2 +
    Math.cos(toRadians(left.lat)) *
      Math.cos(toRadians(right.lat)) *
      Math.sin(longitudeDelta / 2) ** 2;

  return earthRadius * 2 * Math.asin(Math.sqrt(haversine));
};

// Every point that matters has to be on screen the moment the map opens: the
// submitted point, the camera reading it was dragged from, the place the model
// proposed, and the one the owner set himself. Framing on a pair was enough
// while there was only ever a pair.
const getFraming = points => {
  const known = points.filter(Boolean);
  const first = known[0];

  if (known.length < 2) {
    return { center: first, zoom: MAP_ZOOM };
  }

  const lats = known.map(point => point.lat);
  const lngs = known.map(point => point.lng);
  const center = {
    lat: (Math.min(...lats) + Math.max(...lats)) / 2,
    lng: (Math.min(...lngs) + Math.max(...lngs)) / 2,
  };
  // The widest of the two spans, so the box fits whichever way it is stretched.
  const spanMeters = Math.max(
    getDistanceMeters({ lat: Math.min(...lats), lng: center.lng }, { lat: Math.max(...lats), lng: center.lng }),
    getDistanceMeters({ lat: center.lat, lng: Math.min(...lngs) }, { lat: center.lat, lng: Math.max(...lngs) })
  );

  if (spanMeters < 1) {
    return { center, zoom: MAP_ZOOM };
  }

  const metersPerPixel = spanMeters / MAP_FIT_PIXELS;
  const zoom = Math.log2(
    (EQUATOR_METERS_PER_PIXEL * Math.cos((center.lat * Math.PI) / 180)) / metersPerPixel
  );

  return { center, zoom: Math.max(MIN_MAP_ZOOM, Math.min(MAP_ZOOM, Math.floor(zoom))) };
};

// The three points the owner is comparing - where the photo says it was taken,
// where the model wants to put the spot, and where he put it himself - are read
// against satellite imagery, so they get saturated colours nothing on a map is,
// and a label each. Everything else on the map is small and quiet.
const CAMERA_GPS_COLOR = '#ff2ecc';
const PROPOSED_COLOR = '#00b3ff';
const OWNER_COLOR = '#ff2d2d';

const Marker = ({ color, title, label, isSmall }) => (
  <div className={`auto-map-marker ${isSmall ? '--small' : '--strong'}`} title={title}>
    <span className="auto-map-dot" style={{ background: color, borderColor: color }} />
    {label && (
      <span className="auto-map-label" style={isSmall ? undefined : { borderColor: color }}>
        {label}
      </span>
    )}
  </div>
);

// Auto Mod draws its own map instead of reusing components/Map: the only thing
// that matters here is whether the submitted point is believable, so the marker
// is coloured by how the location was obtained.
const LocationMap = ({
  position,
  trust,
  spots = [],
  targetSpotId = '',
  proposedSpot = null,
  correctedPoint = null,
  onPickPoint = null,
}) => {
  if (!position) {
    return (
      <div className="auto-map-empty">
        <strong>No coordinates to show</strong>
        <span>This photo has no usable submitted position.</span>
      </div>
    );
  }

  const originalPoint = toPoint(trust && trust.originalGps);
  const movedOriginal =
    originalPoint &&
    (originalPoint.lat !== position.lat || originalPoint.lng !== position.lng)
      ? originalPoint
      : null;
  // Only worth its own marker when it is somewhere else. A new spot normally
  // lands exactly on the submitted point, and two dots on one pixel say nothing.
  const proposedPoint = toPoint(proposedSpot);
  const movedProposal =
    proposedPoint &&
    (proposedPoint.lat !== position.lat || proposedPoint.lng !== position.lng)
      ? proposedPoint
      : null;
  const ownerPoint = toPoint(correctedPoint);
  // Framed on what the map already knows when it opens, and never on the point
  // the owner clicks: the key below remounts the map when the framing changes,
  // so including his point would throw away his zoom and panning on every click.
  const framing = getFraming([position, movedOriginal, movedProposal]);

  return (
    <div className={`auto-map ${onPickPoint ? '--pickable' : ''}`}>
      {onPickPoint && (
        <p className="auto-map-hint">
          {ownerPoint
            ? 'This spot will be created on your red point. Click again to move it.'
            : 'Click the map to put the spot somewhere else.'}
        </p>
      )}
      <GoogleMap
        onClick={onPickPoint ? ({ lat, lng }) => onPickPoint({ lat, lng }) : undefined}
        // Framing the map is a default, not a leash: the owner zooms in to read
        // the POI labels, and a controlled zoom would drag him back out on every
        // render. The key reframes when the photo or the proposal moves and
        // leaves the map alone in between.
        key={`${framing.center.lat},${framing.center.lng},${framing.zoom}`}
        bootstrapURLKeys={{ key: config.googleMaps.apiKey }}
        defaultCenter={framing.center}
        defaultZoom={framing.zoom}
        options={{
          tilt: 0,
          mapTypeId: 'hybrid',
          mapTypeControl: true,
          streetViewControl: true,
          fullscreenControl: false,
          // The POIs Google draws are the reason this map is worth opening: the
          // spot is named after whatever the buildings around the point are
          // called, and that is what the labels say. clickableIcons false took
          // them off the map entirely, which is the one thing this view cannot
          // afford to lose - Mod has always shown them.
          clickableIcons: true,
          styles: [
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'on' }],
            },
          ],
        }}
      >
        {spots
          .map(spot => ({ spot, point: toPoint(spot) }))
          .filter(entry => entry.point)
          .map(({ spot, point }) => (
            <Marker
              key={spot.id}
              lat={point.lat}
              lng={point.lng}
              color={spot.id === targetSpotId ? '#d9ff72' : '#ffffff'}
              label={spot.name || spot.slug}
              title={`${spot.name || spot.slug} · ${Math.round(
                Number(spot.distanceMeters) || 0
              )} m`}
              isSmall
            />
          ))}

        {movedOriginal && (
          <Marker
            lat={movedOriginal.lat}
            lng={movedOriginal.lng}
            color={CAMERA_GPS_COLOR}
            label="Camera GPS"
            title="Where the camera originally put this photo"
          />
        )}

        <Marker
          lat={position.lat}
          lng={position.lng}
          color={(trust && trust.color) || '#d9ff72'}
          label="Photo point"
          title={(trust && trust.label) || 'Submitted position'}
        />

        {/* Where the spot actually lands. The panel says the point was moved and
            how far, and this is the only place that says onto what ground. */}
        {movedProposal && (
          <Marker
            lat={movedProposal.lat}
            lng={movedProposal.lng}
            color={PROPOSED_COLOR}
            label={proposedSpot.name ? `Proposed · ${proposedSpot.name}` : 'Proposed spot'}
            title={`The proposed spot lands here${
              proposedSpot.movedToPoiName ? `, on ${proposedSpot.movedToPoiName}` : ''
            }`}
          />
        )}

        {/* Where the owner put it by hand, which beats everything else on the
            map. Red because it is the one point that is not a proposal. */}
        {ownerPoint && (
          <Marker
            lat={ownerPoint.lat}
            lng={ownerPoint.lng}
            color={OWNER_COLOR}
            label="Your point"
            title="The spot will be created here"
          />
        )}
      </GoogleMap>
    </div>
  );
};

export default LocationMap;
